"use client";

import { useMemo, useState } from "react";

import { BaseCardClient } from "@/components/base/BaseCard";
import type { BaseWithRelations } from "@/types/base";

import styles from "./StrengthPage.module.css";

type LevelOption = {
  slug: string;
  label: string;
};

type Props = {
  bases: BaseWithRelations[];
  levels: LevelOption[];
  initialLevelSlug?: string | null;
  maxCards?: number;
};

function newestFirst(a: BaseWithRelations, b: BaseWithRelations): number {
  const aDate = Date.parse(a.publish_at || a.created_at || "") || 0;
  const bDate = Date.parse(b.publish_at || b.created_at || "") || 0;
  return bDate - aDate;
}

export default function StrengthBaseBrowser({
  bases,
  levels,
  initialLevelSlug = null,
  maxCards = 8,
}: Props) {
  const validInitialLevel = levels.some((level) => level.slug === initialLevelSlug)
    ? initialLevelSlug
    : null;
  const [activeLevel, setActiveLevel] = useState<string | null>(validInitialLevel);

  const visibleBases = useMemo(() => {
    const filtered = activeLevel
      ? bases.filter((base) => base.level.slug === activeLevel)
      : bases;

    return [...filtered].sort(newestFirst).slice(0, maxCards);
  }, [activeLevel, bases, maxCards]);

  return (
    <div className={styles.browser}>
      <div className={styles.filters} role="group" aria-label="Filter bases by Town Hall">
        <button
          type="button"
          className={styles.filterButton}
          data-active={activeLevel === null}
          aria-pressed={activeLevel === null}
          onClick={() => setActiveLevel(null)}
        >
          All
        </button>

        {levels.map((level) => (
          <button
            key={level.slug}
            type="button"
            className={styles.filterButton}
            data-active={activeLevel === level.slug}
            aria-pressed={activeLevel === level.slug}
            onClick={() => setActiveLevel(level.slug)}
          >
            {level.label}
          </button>
        ))}
      </div>

      {visibleBases.length > 0 ? (
        <div className={styles.baseGrid}>
          {visibleBases.map((base) => (
            <BaseCardClient
              key={base.id}
              base={base}
              showMeta="both"
              showTime
            />
          ))}
        </div>
      ) : (
        <div className={styles.empty}>No bases available for this Town Hall yet.</div>
      )}
    </div>
  );
}
